import { useState, useCallback, useEffect } from "react";
import { Order } from "@/supabase/order/types/order";
import * as orderService from "@/supabase/order/service/orderService";
import { logError } from "@/lib/sentry";
import { RelevantOrdersPanel } from "@/components/RelevantOrdersPanel";

const DEFAULT_CUSTOMER_NICKNAME = "고객 닉네임";

interface RelevantOrdersPanelContainerProps {
  userId: string | undefined;
  activeCustomerId: string | null;
  activeCustomerNickname: string | undefined;
  selectedDate: Date;
  extractOrderId: (customerId: string) => number | null;
  currentOrderTotal: number;
  shippingFeeOverride: number | null;
  setShippingFeeOverride: (value: number | null) => void;
  hasProducts: boolean;
}

/**
 * P07 — relevant-orders 패널의 state/fetch/effect를 Home 밖으로 격리하는 컨테이너.
 * fetch 후 post-await setState 3개(setRelevantOrders/setIsRelevantOrdersPanelOpen/
 * setIsRelevantOrdersLoading)가 Home이 아닌 이 컨테이너만 리렌더하도록 barrier 역할.
 * (Home 리렌더 0이 아니라 "fetch당 post-await 리렌더 격리"가 정확한 효과.)
 */
export function RelevantOrdersPanelContainer({
  userId,
  activeCustomerId,
  activeCustomerNickname,
  selectedDate,
  extractOrderId,
  currentOrderTotal,
  shippingFeeOverride,
  setShippingFeeOverride,
  hasProducts,
}: RelevantOrdersPanelContainerProps) {
  const [isRelevantOrdersPanelOpen, setIsRelevantOrdersPanelOpen] = useState(false);
  const [relevantOrders, setRelevantOrders] = useState<Order[]>([]);
  const [isRelevantOrdersLoading, setIsRelevantOrdersLoading] = useState(false);

  const fetchAndShowRelevantOrdersPanel = useCallback(async (nickname: string) => {
    if (!userId || !nickname || nickname === DEFAULT_CUSTOMER_NICKNAME) {
      setIsRelevantOrdersPanelOpen(false);
      return;
    }
    setIsRelevantOrdersLoading(true);
    try {
      const excludeOrderId = activeCustomerId ? extractOrderId(activeCustomerId) : null;
      const orders = await orderService.findRecentRelevantOrdersByNickname(userId, nickname, selectedDate, excludeOrderId);
      setRelevantOrders(orders);
      setIsRelevantOrdersPanelOpen(orders.length > 0);
    } catch (error) {
      logError("주문 내역 조회 실패:", error);
      setIsRelevantOrdersPanelOpen(false);
    } finally {
      setIsRelevantOrdersLoading(false);
    }
  }, [userId, activeCustomerId, selectedDate, setIsRelevantOrdersPanelOpen, setRelevantOrders, setIsRelevantOrdersLoading, extractOrderId]);

  const handleCloseRelevantOrdersPanel = useCallback(() => {
    setIsRelevantOrdersPanelOpen(false);
  }, [setIsRelevantOrdersPanelOpen]);

  useEffect(() => {
    if (activeCustomerNickname) {
      fetchAndShowRelevantOrdersPanel(activeCustomerNickname);
    } else {
      setIsRelevantOrdersPanelOpen(false);
    }
  }, [activeCustomerNickname, selectedDate, fetchAndShowRelevantOrdersPanel]);

  // Align the right-bottom panel with the right edge of the message preview container
  useEffect(() => {
    const updateRightOffset = () => {
      const msg = document.getElementById('message-section');
      const panel = document.getElementById('relevant-orders-panel');
      if (!msg || !panel) return;

      const msgRect = msg.getBoundingClientRect();
      const viewportRight = window.innerWidth;
      const desiredRight = Math.max(20, Math.round(viewportRight - msgRect.right)); // px
      document.documentElement.style.setProperty('--relevant-panel-right', `${desiredRight}px`);
    };

    updateRightOffset();
    window.addEventListener('resize', updateRightOffset);
    return () => window.removeEventListener('resize', updateRightOffset);
  }, [isRelevantOrdersPanelOpen]);

  return (
    <RelevantOrdersPanel
      isOpen={isRelevantOrdersPanelOpen}
      isLoading={isRelevantOrdersLoading}
      orders={relevantOrders}
      nickname={activeCustomerNickname || ''}
      orderId={activeCustomerId || undefined}
      currentOrderTotal={currentOrderTotal}
      shippingFeeOverride={shippingFeeOverride}
      setShippingFeeOverride={setShippingFeeOverride}
      onClose={handleCloseRelevantOrdersPanel}
      hasProducts={hasProducts}
    />
  );
}
